// pages/InvoiceDetails.tsx
import React, { useEffect, useState } from 'react';
import styles from './InvoiceDetails.module.scss';
import Button from '../../components/common/button/Button';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import invoiceService from '../../services/invoiceService';
import companyService from '../../services/companyService';
import { Invoice } from '../../types/invoiceTypes';
import { Company } from '../../types/companyTypes';
import { useParams, useNavigate } from 'react-router-dom';

const InvoiceDetails: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const navigate = useNavigate();

  const fetchCompany = async () => {
    try {
      const response = await companyService.getCompany('1');
      
      if (response && typeof response === 'object') {
        const normalizedCompany: Company = {
          id: response.id ?? '',
          name: response.name ?? '',
          email: response.email ?? '',
          phone: response.phone ?? '',
          address: response.address ?? '',
          city: response.city ?? '',
          state: response.state ?? '',
          zip_code: response.zip_code ?? '',
          country: response.country ?? '',
          website: response.website ?? '',
          gst: response.gst ?? '',
          is_active: response.is_active ?? true,
          created_at: response.created_at ?? new Date().toISOString(),
          updated_at: response.updated_at ?? new Date().toISOString(),
        };
        setCompany(normalizedCompany);
      }
    } catch (err) {
      console.error('Error fetching company:', err);
      toast.error('Failed to fetch company details');
    }
  };

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      if (id) {
        const data = await invoiceService.getInvoiceById(id);
        setInvoice(data);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompany();
    if (id) {
      fetchInvoiceDetails();
    }
  }, [id]);

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading invoice details...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Invoice Not Found</h2>
          <p>The invoice you're looking for doesn't exist or has been removed.</p>
          <Button variant="primary" onClick={handleGoBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Calculate values same as InvoiceList page
  const cgstAmount = invoice.cgst_amount || 0;
  const sgstAmount = invoice.sgst_amount || 0;
  const discountAmount = invoice.discount_amount || 0;
  const totalAmount = invoice.total_amount;

  // Same subtotal calculation as InvoiceList: subtotal = total_amount + discount_amount - (cgst + sgst)
  const getSubTotal = (amount: number, discount_amount: number, cgst_amount: number, sgst_amount: number) => {
    const subtotal = amount + discount_amount - (cgst_amount + sgst_amount);
    return subtotal;
  };

  const subtotal = getSubTotal(Number(totalAmount), Number(discountAmount), Number(cgstAmount), Number(sgstAmount));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getInvoicePrintContent = (withTax: boolean) => {
    if (!invoice) return '';

    const companyName = company?.name && company.name.length > 0 ? company.name : 'Company Name';
    const companyAddressParts = [
      company?.address,
      company?.city,
      company?.state,
      company?.zip_code,
      company?.country,
    ].filter(Boolean);
    const companyAddress = companyAddressParts.join(', ');

    return `
      <div style="text-align:center; margin-bottom:8px;">
        <h1 style="margin-bottom:0; font-size:1.4rem; color:green;">
          ${companyName}
        </h1>
        ${companyAddress ? `<div style="font-size:1rem; margin-bottom:4px;">${companyAddress}</div>` : ''}
        ${company?.phone ? `<div>Contact: ${company.phone}</div>` : ''}
        ${company?.email ? `<div>Email: ${company.email}</div>` : ''}
        ${company?.website ? `<div>Website: ${company.website}</div>` : ''}
        ${company?.gst ? `<div>GST: ${company.gst}</div>` : ''}
      </div>

      <div style="border-top:2px solid #333; border-bottom:2px solid #333; padding:8px 0; margin:16px 0;">
        <h2 style="text-align:center; margin:0; font-size:1.3rem;">${invoice.invoice_number}</h2>
      </div>

      <div style="display:flex; justify-content:space-between; margin-bottom:16px;">
        <div>
          <div><strong>Date:</strong> ${new Date(invoice.created_at).toLocaleDateString('en-IN')}</div>
          <div><strong>Time:</strong> ${new Date(invoice.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
        <div style="text-align:right;">
          <div><strong>Bill To:</strong></div>
          <div>${invoice.customer_name}</div>
          ${invoice.customer_phone ? `<div>Phone: ${invoice.customer_phone}</div>` : ''}
          ${invoice.customer_address ? `<div>${invoice.customer_address}</div>` : ''}
          ${invoice.gst ? `<div>GST: ${invoice.gst}</div>` : ''}
        </div>
      </div>

      <table style="width:100%; border-collapse:collapse; margin-bottom:16px;">
        <thead>
          <tr style="background:#f5f5f5;">
            <th style="border:1px solid #ccc; padding:8px; text-align:left;">S.No</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:left;">Description</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:center;">Quantity</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:right;">Unit Price</th>
            <th style="border:1px solid #ccc; padding:8px; text-align:right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items?.map((item, index) => {
            let unitPrice, amount;
            
            if (withTax) {
              // With tax breakup - show base prices (excluding tax)
              // unit_price and amount from DB are tax-inclusive, so we need to extract base price
              const itemTotal = Number(item.amount);
              const baseAmount = itemTotal / 1.18; // Remove 18% tax to get base
              const baseUnitPrice = baseAmount / Number(item.quantity);
              
              unitPrice = baseUnitPrice.toFixed(2);
              amount = baseAmount.toFixed(2);
            } else {
              // Without tax breakup - show tax-inclusive prices directly
              unitPrice = Number(item.unit_price).toFixed(2);
              amount = Number(item.amount).toFixed(2);
            }

            return `
              <tr>
                <td style="border:1px solid #ccc; padding:8px;">${index + 1}</td>
                <td style="border:1px solid #ccc; padding:8px;">${item.description}</td>
                <td style="border:1px solid #ccc; padding:8px; text-align:center;">${item.quantity}</td>
                <td style="border:1px solid #ccc; padding:8px; text-align:right;">₹${unitPrice}</td>
                <td style="border:1px solid #ccc; padding:8px; text-align:right;">₹${amount}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>

      <div style="display:flex; justify-content:flex-end; margin-bottom:16px;">
        <div style="min-width:300px;">
          ${withTax ? `
            <div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid #eee;">
              <span>Subtotal:</span>
              <span>₹${Number(subtotal).toFixed(2)}</span>
            </div>
            <div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid #eee;">
              <span>CGST (9%):</span>
              <span>₹${Number(cgstAmount).toFixed(2)}</span>
            </div>
            <div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid #eee;">
              <span>SGST (9%):</span>
              <span>₹${Number(sgstAmount).toFixed(2)}</span>
            </div>
          ` : ''}
          ${Number(discountAmount) > 0 ? `
            <div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid #eee;">
              <span>Discount:</span>
              <span>-₹${Number(discountAmount).toFixed(2)}</span>
            </div>
          ` : ''}
          <div style="display:flex; justify-content:space-between; padding:10px 0; font-size:1.2rem; font-weight:bold; border-top:2px solid #333; margin-top:8px;">
            <span>Total Amount:</span>
            <span>₹${Number(totalAmount).toFixed(2)}</span>
          </div>
        </div>
      </div>

      ${invoice.notes ? `
        <div style="margin-top:24px; padding:12px; background:#f9fafb; border:1px solid #e5e7eb; border-radius:4px;">
          <strong>Notes:</strong><br/>
          ${invoice.notes}
        </div>
      ` : ''}

      <div style="margin-top:40px; padding-top:16px; border-top:1px solid #ccc; text-align:center; font-size:0.9rem; color:#666;">
        <p style="margin:0;">Thank you for purchasing!</p>
      </div>
    `;
  };

  const handlePrintInvoice = (withTax: boolean) => {
    const printWindow = window.open('', '', 'height=800,width=1100');
    if (printWindow) {
      printWindow.document.write(`<html><head><title>#${invoice.invoice_number}</title>`);
      printWindow.document.write(`
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            font-size: 14px;
            line-height: 1.5;
          }
          table { 
            border-collapse: collapse; 
            width: 100%; 
          }
          th, td { 
            border: 1px solid #ccc; 
            padding: 8px; 
          }
          th { 
            background: #f5f5f5;
            font-weight: bold;
          }
          @media print {
            body { margin: 10px; }
            @page { 
              margin: 0.5cm;
              size: auto;
            }
          }
        </style>
      `);
      printWindow.document.write('</head><body>');
      printWindow.document.write(getInvoicePrintContent(withTax));
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
    setShowPrintOptions(false);
  };

  const getStatusBadge = (status: string) => {
    const badges: any = {
      draft: { color: '#94a3b8', bg: '#f1f5f9' },
      pending: { color: '#f59e0b', bg: '#fef3c7' },
      paid: { color: '#10b981', bg: '#d1fae5' },
      partial: { color: '#3b82f6', bg: '#dbeafe' },
      cancelled: { color: '#ef4444', bg: '#fee2e2' }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={styles.status} style={{
        backgroundColor: badge.bg,
        color: badge.color
      }}>
        {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button variant="secondary" onClick={handleGoBack} className={styles.backButton}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Invoices
          </Button>
          <div className={styles.titleSection}>
            <h1>Invoice Details</h1>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div style={{ position: 'relative' }}>
            <Button onClick={() => setShowPrintOptions(!showPrintOptions)} className={styles.printButton}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <polyline points="6 14 18 14 18 22 6 22 6 14"/>
              </svg>
              Print Invoice
            </Button>
            
            {showPrintOptions && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                minWidth: '200px'
              }}>
                <button
                  onClick={() => handlePrintInvoice(true)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    borderBottom: '1px solid #e2e8f0'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Print with Tax Breakup
                </button>
                <button
                  onClick={() => handlePrintInvoice(false)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Print without Tax Breakup
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Info Cards */}
      <div className={styles.infoCards}>
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <h3>Invoice Information</h3>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Invoice Number:</span>
              <span className={styles.value}>{invoice.invoice_number}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Created Date:</span>
              <span className={styles.value}>{new Date(invoice.created_at).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Created Time:</span>
              <span className={styles.value}>{new Date(invoice.created_at).toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit'
              })}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Created By:</span>
              <span className={styles.value}>{invoice.employee_name || 'System'}</span>
            </div>
          </div>
        </div>

        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <h3>Customer Information</h3>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Customer Name:</span>
              <span className={styles.value}>{invoice.customer_name}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Phone:</span>
              <span className={styles.value}>{invoice.customer_phone || 'N/A'}</span>
            </div>
            {invoice.customer_address && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Address:</span>
                <span className={styles.value}>{invoice.customer_address}</span>
              </div>
            )}
            {invoice.gst && (
            <div className={styles.infoRow}>
              <span className={styles.label}>GST:</span>
              <span className={styles.value}>{invoice.gst}</span>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Items - showing tax-inclusive prices directly (no extra calculation) */}
      <div className={styles.invoiceItems}>
        <h3>Invoice Items</h3>
        <div className={styles.itemsTable}>
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items?.map((item, index) => {
                // Show unit_price and amount directly - they are already tax-inclusive
                return (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td>
                      <div className={styles.productInfo}>
                        <span className={styles.productName}>{item.description}</span>
                        {item.product_name && item.product_name !== item.description && (
                          <span className={styles.productDescription}>{item.product_name}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={styles.quantity}>{item.quantity}</span>
                    </td>
                    <td>{formatCurrency(item.unit_price)}</td>
                    <td>{formatCurrency(item.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Summary - Same structure as InvoiceList */}
      <div className={styles.invoiceSummary}>
        <div className={styles.summaryContent}>
          <div className={styles.summaryLeft}>
            {invoice.notes && (
              <div className={styles.summaryNote}>
                <h4>Notes</h4>
                <p>{invoice.notes}</p>
              </div>
            )}
          </div>
          <div className={styles.summaryRight}>
            <div className={styles.totals}>
              <div className={styles.totalRow}>
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className={styles.totalRow}>
                <span>CGST (9%):</span>
                <span>{formatCurrency(cgstAmount)}</span>
              </div>
              <div className={styles.totalRow}>
                <span>SGST (9%):</span>
                <span>{formatCurrency(sgstAmount)}</span>
              </div>
              {discountAmount > 0 && (
                <div className={styles.totalRow}>
                  <span>Discount:</span>
                  <span>-{formatCurrency(discountAmount)}</span>
                </div>
              )}
              <div className={styles.divider}></div>
              <div className={`${styles.totalRow} ${styles.grandTotal}`}>
                <span>Total Amount:</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPrintOptions && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setShowPrintOptions(false)}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default InvoiceDetails;